import type { Catalogue, CatalogueNight, Level, Observatory } from '@astrasonora/annotation-schema'
import { DEFAULT_PARAMS, OBSERVATORIES } from '@astrasonora/annotation-schema'
import { hashHex } from './hash.ts'
import { lastOffered, type PlayerState } from './state.ts'

export function catalogueNight(cat: Catalogue, o: Observatory, n: Level): CatalogueNight | null {
  const obs = cat.observatories.find((x) => x.observatory === o)
  return obs?.nights.find((l) => l.n === n) ?? null
}

/** Non-demo songs of the night in the observatory, current snapshot. */
export function N(cat: Catalogue, o: Observatory, n: Level): number {
  return catalogueNight(cat, o, n)?.songIds.length ?? 0
}

/**
 * Charted songs that meet the night in the observatory: set per observatory and night by the
 * curator, carried in the snapshot (spec §8); 0 where the family has nothing there.
 */
export function needed(cat: Catalogue, o: Observatory, n: Level): number {
  return catalogueNight(cat, o, n)?.needed ?? DEFAULT_PARAMS.neededDefault
}

/** Charted songs counted where they were charted (spec §9), whether or not still in the snapshot. */
export function cleared(state: PlayerState, o: Observatory, n: Level): number {
  let count = 0
  for (const s of Object.values(state.songs))
    if (s.status === 'charted' && s.chartedObservatory === o && s.chartedLevel === n) count++
  return count
}

/**
 * Charted constellations among the night's current songs of the observatory: what its column
 * shows (spec §8). Display only; the threshold is `cleared`, which also counts songs since withdrawn or moved.
 */
export function charted(state: PlayerState, cat: Catalogue, o: Observatory, n: Level): number {
  const night = catalogueNight(cat, o, n)
  if (!night) return 0
  return night.songIds.filter((id) => state.songs[id]?.status === 'charted').length
}

/** Every constellation of the observatory's night is charted and there is at least one. */
export function nightComplete(
  state: PlayerState,
  cat: Catalogue,
  o: Observatory,
  n: Level,
): boolean {
  const total = N(cat, o, n)
  return total > 0 && charted(state, cat, o, n) >= total
}

/** `met(o, n)`: the observatory's count for the night is reached (spec §8). */
export function met(state: PlayerState, cat: Catalogue, o: Observatory, n: Level): boolean {
  return cleared(state, o, n) >= needed(cat, o, n)
}

/** A night ends when both observatories have met it (spec §8). */
export function nightMet(state: PlayerState, cat: Catalogue, n: Level): boolean {
  return OBSERVATORIES.every((o) => met(state, cat, o, n))
}

/** The one night the player has reached (spec §8). */
export function unlockedLevel(state: PlayerState): Level {
  return state.night.unlocked
}

/**
 * The sky charted (visual §6): the last night is open and both observatories have met it. There is
 * no night to deepen into; the story of the ending plays instead.
 */
export function skyCharted(state: PlayerState, cat: Catalogue): boolean {
  return unlockedLevel(state) === 7 && nightMet(state, cat, 7)
}

/** The last night is open and every night of either observatory that holds constellations is fully charted: dawn (visual §5). */
export function skyComplete(state: PlayerState, cat: Catalogue): boolean {
  if (unlockedLevel(state) < 7) return false
  let any = false
  for (const obs of cat.observatories)
    for (const night of obs.nights) {
      if (night.songIds.length === 0) continue
      any = true
      if (!nightComplete(state, cat, obs.observatory, night.n)) return false
    }
  return any
}

/** The hour of a night, from 0 at Dusk to 6/7 at Before dawn; 1 is dawn, which no night reaches (visual §5). */
export function nightHour(n: Level): number {
  return (n - 1) / 7
}

/** Deterministic, stable song order per player (spec §8): sorted by hash(playerId:songId). */
function orderKey(playerId: string, songId: string): string {
  return hashHex(`${playerId}:${songId}`)
}

export function songOrder(playerId: string, songIds: readonly string[]): string[] {
  return [...songIds]
    .map((id) => ({ id, key: orderKey(playerId, id) }))
    .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
    .map((x) => x.id)
}

function unresolved(state: PlayerState, songIds: readonly string[]): string[] {
  return songIds.filter((id) => state.songs[id]?.status !== 'charted')
}

/**
 * The next song to offer at a night in an observatory (spec §8): the first unresolved song in
 * circular key order strictly after the last offered one; with two or more unresolved this is
 * never the last offered.
 */
export function next(state: PlayerState, cat: Catalogue, o: Observatory, n: Level): string | null {
  const night = catalogueNight(cat, o, n)
  if (!night) return null
  const open = new Set(unresolved(state, night.songIds))
  if (open.size === 0) return null
  const order = songOrder(state.playerId, night.songIds)
  const last = lastOffered(state, o, n)
  const lastKey = last ? orderKey(state.playerId, last) : ''
  const startAt = order.findIndex((id) => orderKey(state.playerId, id) > lastKey)
  const rotated = startAt < 0 ? order : [...order.slice(startAt), ...order.slice(0, startAt)]
  return rotated.find((id) => open.has(id)) ?? null
}

/**
 * `behind(n)` (spec §8): the observatory with the smaller share of its count reached; one with a
 * count of zero, or with nothing left to offer, is never behind; a tie goes to the major.
 */
export function behind(
  state: PlayerState,
  cat: Catalogue,
  n: Level,
  offers: (o: Observatory) => boolean,
): Observatory | null {
  let best: Observatory | null = null
  let bestShare = Infinity
  for (const o of OBSERVATORIES) {
    if (!offers(o)) continue
    const need = needed(cat, o, n)
    const share = need === 0 ? Infinity : cleared(state, o, n) / need
    if (share < bestShare) {
      bestShare = share
      best = o
    }
  }
  return best
}

export interface Featured {
  observatory: Observatory
  songId: string
}

/**
 * The featured card of a night (spec §8): the next song to chart of the observatory that is
 * behind among those with an unresolved song; null when the night is fully charted. Demo songs
 * are never featured: they wait, charted, in the night's columns, to be listened to or not.
 */
export function featured(state: PlayerState, cat: Catalogue, n: Level): Featured | null {
  const o = behind(state, cat, n, (o) => next(state, cat, o, n) !== null)
  if (!o) return null
  const songId = next(state, cat, o, n)
  return songId ? { observatory: o, songId } : null
}
